import { Injectable } from "@angular/core";
import { CalendarWeek, PlanningAssistantOperation } from "../../../_models/planning-assistant.interface";
import { PlanningAssistantService } from "../../../_services/planning-assistant.service";
import { PATechnician } from "./technician";
import { PATimeControl } from "../singletons/pa-time-control";
import { PAUtil } from "./util";
import { PAOperation } from "./operation";
import { PADataControl } from "../singletons/pa-data-control";

@Injectable({
  providedIn: 'root',
})
export class TechnicianDataLoader {

  readonly calendarWeekDataMap = new Map<string, { user_id: number, operations: PlanningAssistantOperation[] }[]>
  readonly calendarWeekLoadedAtTimestampMap = new Map<string, number>();

  readonly loadingCalendarWeekKeys: string[] = []

  reloadAllNMinutes = 5

  private technicians: PATechnician[] = []

  constructor(
    private planningAssistantService: PlanningAssistantService
  ) {
  }

  setTechnicians(techs: PATechnician[]) {
    this.technicians = techs;
  }

  calendarWeekToKey(cw: CalendarWeek): string {
    return `${cw.number}-${cw.year}`
  }

  async loadDataForCalendarWeek(cw: CalendarWeek) {
    const cw_key = this.calendarWeekToKey(cw)
    const cw_is_loading = this.loadingCalendarWeekKeys.includes(cw_key)
    if (!cw_is_loading && !this.calendarWeekWasLoadedInTheLastNMinutes(cw, this.reloadAllNMinutes)) {
      this.loadingCalendarWeekKeys.push(cw_key)
      const calendar_week_data = PATimeControl.Instance.calculateCalendarWeekData(cw.number, cw.year)
      const technician_ids = this.technicians.map(t => t.id)
      const date_from = PATimeControl.Instance.dateToDatestring(new Date(calendar_week_data.weekdays['monday'].timestamp), true, false)
      const date_until = PATimeControl.Instance.dateToDatestring(new Date(calendar_week_data.weekdays['sunday'].timestamp), true, false)
      return new Promise<void>(resolve => {
        this.planningAssistantService.getOperationUserInfo(technician_ids, date_from + " 00:00:00", date_until + " 23:59:59", {exclude_material_return: true}).subscribe(
          data => {
            this.calendarWeekDataMap.set(cw_key, data)
            this.calendarWeekLoadedAtTimestampMap.set(cw_key, Date.now())
            this.removeLoadingCWKey(cw_key)
            resolve()
          },
          error => {
            console.log(error)
            this.removeLoadingCWKey(cw_key)
            resolve()
          }
        )
      })
    } else if (cw_is_loading) {
      await PAUtil.sleepUntilConditionBreaks(
        () => {
          return this.loadingCalendarWeekKeys.includes(cw_key)
        }
      )
    }
  }

  calendarWeekWasLoadedInTheLastNMinutes(cw: CalendarWeek, minutes: number) {
    const last_ts = this.calendarWeekLoadedAtTimestampMap.get(this.calendarWeekToKey(cw))
    if (last_ts) {
      return Date.now() - last_ts <= minutes * 60 * 1000
    } else {
      return false
    }
  }

  removeLoadingCWKey(cw_key: string): void {
    PAUtil.removeElementFromList<string>(this.loadingCalendarWeekKeys, cw_key)
  }

  async getUserInfosForCalendarWeek(cw: CalendarWeek): Promise<{ user_id: number, operations: PlanningAssistantOperation[] }[]> {
    if (!this.calendarWeekWasLoadedInTheLastNMinutes(cw, this.reloadAllNMinutes)) {
      await this.loadDataForCalendarWeek(cw)
    }
    return this.calendarWeekDataMap.get(this.calendarWeekToKey(cw))
  }

  async getTechniciansOperationsForDayTimestamp(technician: PATechnician, utc_timestamp: number): Promise<PAOperation[]> {
    const cw = PATimeControl.Instance.timestampToCalendarWeek(utc_timestamp)
    if (!this.calendarWeekWasLoadedInTheLastNMinutes(cw, this.reloadAllNMinutes)) {
      await this.loadDataForCalendarWeek(cw)
    }
    const entry = this.calendarWeekDataMap.get(this.calendarWeekToKey(cw)).find(item => item.user_id == technician.id)
    const pa_operations = entry ? entry.operations.map(op_hash => PADataControl.Instance.operationHashToOperation(op_hash, true)) : []
    PADataControl.Instance.addOperationsToLoadedOperations(pa_operations)

    return pa_operations.filter(operation => operation.getPlannedDayTimestamp() == utc_timestamp)
  }
}
